// Per-step header shown beneath the MultiAlbumProgress strip.
//
// Spec: docs/superpowers/specs/2026-06-04-multi-album-flow-design.md
//
// PURPOSE
// Replaces the generic "Étape X sur Y" pattern with a concrete, personal
// title that names the customer's own destination — "Couverture · Bali"
// reads as THEIR album, not a procedural step. Per ui-ux-pro-max:ux,
// concrete > procedural for completion rates.
//
// Smaller "Album N sur M" sub-line is the secondary anchor for customers
// who DO want to see "where am I in the journey" — but lower hierarchy so
// the destination is the eye magnet.

const C = {
  gold:      '#C9A24A',
  goldDeep:  '#A8842F',
  dark:      '#1F2937',
  muted:     '#6B7280',
};

export interface AlbumPhaseHeaderProps {
  /** The action the customer is doing right now, in French. Example:
   *  "Couverture", "Photos", "Aperçu", "Édition". */
  action: string;
  /** Per-album destination, e.g. "Bali". Highlighted in gold so the customer
   *  registers "this is for MY album, not just any album". Optional because
   *  the mode-choice and finalisation steps aren't per-album. */
  destination?: string;
  /** 1-indexed album the customer is on. Omit for non-per-album steps. */
  albumIndex?: number;
  /** Total albums in the bundle. Omit for non-per-album steps. */
  totalAlbums?: number;
  /** Optional one-line explainer rendered under the title in muted text. */
  hint?: string;
}

export function AlbumPhaseHeader({
  action,
  destination,
  albumIndex,
  totalAlbums,
  hint,
}: AlbumPhaseHeaderProps) {
  const showAlbumCount = albumIndex != null && totalAlbums != null && totalAlbums > 1;
  return (
    <header className="max-w-5xl mx-auto px-4 pt-6 pb-3">
      {showAlbumCount && (
        <p className="text-[11px] font-semibold uppercase tracking-widest mb-1.5" style={{ color: C.goldDeep, letterSpacing: '0.12em' }}>
          Album {albumIndex} sur {totalAlbums}
        </p>
      )}
      <h1
        className="text-2xl sm:text-3xl font-bold leading-tight"
        style={{
          color: C.dark,
          fontFamily: "'Fraunces', 'Cormorant Garamond', Georgia, serif",
        }}
      >
        {action}
        {destination ? (
          <>
            <span className="mx-2 font-light" style={{ color: `${C.gold}80` }}>·</span>
            <span style={{ color: C.goldDeep }}>{destination}</span>
          </>
        ) : null}
      </h1>
      {hint && (
        <p className="text-sm mt-2 leading-relaxed" style={{ color: C.muted }}>
          {hint}
        </p>
      )}
    </header>
  );
}
